import ReactModal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { useRef, useEffect } from 'preact/hooks';

import {
  Header as HeaderModal,
  Body as BodyModal,
  Content as ContentModal,
} from 'components/Modal';
import CloseButton from 'components/CloseButton';
import LoadingDots from 'components/LoadingDots';
import Shipment from 'interfaces/PmParcel';

import {
  Text,
  Button,
  FailedNotificationText,
  reactModalStyle,
} from './SubscriptionModal.styles';
import { useSubscription } from './useSubscription';

interface SubscriptionModalProps {
  organizationId: number;
  selectedShipment: Shipment;
}

export const SubscriptionModal = ({
  organizationId,
  selectedShipment,
}: SubscriptionModalProps): JSX.Element => {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  const {
    showSubscriptionModal,
    isResubscribing,
    isResubscribedFail,
    handleResubscribe,
    handleCloseSubscriptionModal,
  } = useSubscription(organizationId, selectedShipment);

  useEffect(() => {
    if (closeButtonRef.current && showSubscriptionModal) {
      closeButtonRef.current.focus();
    }
  }, [showSubscriptionModal]);

  // TODO: Use ModalV2
  return (
    <ReactModal
      isOpen={showSubscriptionModal}
      style={reactModalStyle}
      onRequestClose={handleCloseSubscriptionModal}
      parentSelector={() => {
        return document.querySelector('#portalWrapper') || document.body;
      }}
      ariaHideApp={false}
      aria={{
        labelledby: 'subscription-modal-heading',
        describedby: 'subscription-modal-description',
      }}
    >
      <HeaderModal>
        <div id="subscription-modal-heading">{t('UNSUBSCRIBE_MESSAGE_1')}</div>
        <CloseButton
          ref={closeButtonRef}
          onClick={handleCloseSubscriptionModal}
          aria-label={t('ALT_TEXT_CLOSE_MODAL')}
        />
      </HeaderModal>
      <BodyModal>
        <ContentModal>
          <Text id="subscription-modal-description">
            {t('UNSUBSCRIBE_MESSAGE_2')}
          </Text>
          <Button
            data-testid="resub-button"
            disabled={isResubscribing}
            onClick={handleResubscribe}
          >
            {isResubscribing ? (
              <span>
                {t('RESUBSCRIBING')}
                <LoadingDots />
              </span>
            ) : (
              t('RESUB_BUTTON_TEXT')
            )}
          </Button>
          {isResubscribedFail && (
            <FailedNotificationText data-testid="fail-text">
              {t('PLEASE_TRY_AGAIN')}
            </FailedNotificationText>
          )}
        </ContentModal>
      </BodyModal>
    </ReactModal>
  );
};
